import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import { User } from './user.model';
import cookie, {
  COOKIE,
  COOKIE_EXPIRATION,
  updateDefaultRole,
  getSource,
} from '@/utils/cookie';
import { getSubscriptionStatus } from '@/utils/subscription/subscription.util';
import { identifyUser } from '../../utils/mixpanel-analytics/util.mp';

export interface SigninParams {
  email: string;
  password: string;
}

export interface ResetParams {
  email: string;
  newPassword: string;
  token: string;
}

export interface RequestResetParams {
  email: string;
}

export interface SignupParams {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  school: string;
  password: string;
}

export interface AddPhoneNumParams {
  phoneNumber: string;
  token: string;
}

export interface UserStoreInterface {
  user: User | null;
  error: ErrorResponse | null;
  fetchUser(): Promise<void>;
  updateProfile(params: User): Promise<void>;
  signin(params: SigninParams): Promise<void>;
  signup(params: SignupParams): Promise<void>;
  signout(): Promise<void>;
  reset(params: ResetParams): Promise<void>;
  requestReset(params: RequestResetParams): Promise<void>;
  addPhoneNumber(params: AddPhoneNumParams): Promise<void>;
  postReview(description: string): Promise<void>;
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'user',
  store,
})
class UserStore extends VuexModule {
  user: User | null = null;
  error: ErrorResponse | null = null;

  @Mutation
  updateUser(params: User) {
    this.user = params;
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Action
  async fetchUser(): Promise<void> {
    this.context.commit('updateUser', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(`users/me`);

      this.context.commit('updateUser', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async updateProfile(params: User): Promise<void> {
    this.context.commit('updateUser', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.put(`users`, params);

      this.context.commit('updateUser', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async addPhoneNumber(params: AddPhoneNumParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.put(`profile/update-phone-number/${params.token}`, {
        phoneNumber: params.phoneNumber,
      });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async signin(params: SigninParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.post(`sessions`, {
        ...params,
      });

      cookie.set(COOKIE.TOKEN, data.token, {
        sameSite: 'strict',
        expires: COOKIE_EXPIRATION,
      });

      updateDefaultRole(data.role);

      cookie.set(COOKIE.ROLE, data.role, {
        sameSite: 'strict',
        expires: COOKIE_EXPIRATION,
      });
      cookie.set(COOKIE.EMAIL, data.email, {
        sameSite: 'strict',
        expires: COOKIE_EXPIRATION,
      });

      this.context.commit('updateUser', data);
      await getSubscriptionStatus();

      identifyUser(
        params.email,
        data.firstName,
        data.firstName,
        data.phone,
        data.userStatus,
        data.role,
      );
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async signup(params: SignupParams): Promise<void> {
    this.context.commit('setError', null);

    const source = getSource() || null;

    try {
      const fbEvent = (window as any).fbq;
      console.log('fbEvent:', fbEvent);
      fbEvent('track', 'CompleteRegistration');

      const { data }: CustomAxiosResponse = await axios.post(`users`, {
        ...params,
        source,
      });

      this.context.commit('updateUser', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async signout(): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.delete(`sessions`);
      cookie.remove(COOKIE.TOKEN, { sameSite: 'Strict' });
      cookie.remove(COOKIE.ROLE, { sameSite: 'Strict' });
      cookie.remove(COOKIE.EMAIL, { sameSite: 'Strict' });
      cookie.remove(COOKIE.STATUS, { sameSite: 'Strict' });
      cookie.remove(COOKIE.IS_SUSPENDED, { sameSite: 'Strict' });
      cookie.remove(COOKIE.SEEN_TUTORIAL, { sameSite: 'Strict' });
      cookie.remove(COOKIE.CLOSE_STUDY_GROUP, { sameSite: 'Strict' });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async reset(params: ResetParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.post(`forgot-password/reset`, params);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async requestReset(params: RequestResetParams): Promise<void> {
    this.context.commit('setError', null);

    try {
      await axios.post(`forgot-password`, params);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async postReview(description: string): Promise<void> {
    this.context.commit('updateUser', null);
    this.context.commit('setError', null);

    try {
      await axios.post(`users/reviews`, { description });
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export default getModule(UserStore);
